export type MarketPipelineStage = 'open' | 'resolving' | 'resolved';

export interface ConvictionLeader {
  readonly outcomeIndex: number;
  /** Live conviction as a fraction of 1 (PPB ÷ 1e9). */
  readonly conviction: number;
}

/**
 * Market lifecycle as clients render it.
 *
 * On chain a market moves Backing → Trading → Resolved → Graduated, and
 * resolution is one atomic step. There is no confirmation window.
 *
 * - `open` — trading with no outcome at the threshold, or not tradeable yet.
 * - `resolving` — still `trading` on chain but the leading outcome's live
 *   conviction has reached the threshold; the next trade or crank settles it.
 * - `resolved` — resolved or graduated: the winner is known and claims are open.
 */
export function computeMarketPipelineStage(
  status: string,
  leaderConviction: number | null,
  threshold: number | null,
): MarketPipelineStage {
  if (status === 'resolved' || status === 'graduated') return 'resolved';
  if (status !== 'trading' || leaderConviction === null || threshold === null || threshold <= 0) {
    return 'open';
  }
  return leaderConviction >= threshold ? 'resolving' : 'open';
}

/** Fractional distance from live conviction to the resolution line. */
export function projectedResolutionGap(threshold: number, conviction: number | undefined): number {
  const share: number = conviction === undefined || !Number.isFinite(conviction) ? 0 : conviction;
  return Math.max(0, threshold - share);
}

/** Highest live conviction; ties go to the lowest outcome index. */
export function selectConvictionLeader(
  liveConviction: ReadonlyMap<number, number>,
): ConvictionLeader | null {
  let leader: ConvictionLeader | null = null;
  for (const [outcomeIndex, conviction] of liveConviction) {
    if (!Number.isFinite(conviction)) continue;
    if (
      leader === null ||
      conviction > leader.conviction ||
      (conviction === leader.conviction && outcomeIndex < leader.outcomeIndex)
    ) {
      leader = { outcomeIndex, conviction };
    }
  }
  return leader;
}
